<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import ModalDeBusca from '@/components/base/modal/ModalDeBusca.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import api from '@/services/api'
import { criarConfigBuscaProduto } from '@/search-definitions/produtoSearch'
import { PO_PRODUTO_ST } from '@/constants/PO_PRODUTO_ST'
import { useNavegacao } from '@/composables/useNavegacao'
import { useConversaoQuantidade } from '@/composables/useConversaoQuantidade'

const props = defineProps({
  produtosComSaldo: { type: Array, default: () => [] },
  produtosPurchaseOrder: { type: Array, default: () => [] },
  listas: { type: Object, default: () => ({}) },
  podeEditar: { type: Boolean, default: false },
  isPosAprovacao: { type: Boolean, default: false },
  accordionFechado: { type: Boolean, default: false },
  idPurchaseOrder: { type: Number, default: null },
  percentualToleranciaPadrao: { type: Number, default: null },
  lstMoeda: { type: String, default: '' },
  modoAlocacao: { type: String, default: 'saldo' },
  idParceiroNegocioFornecedor: { type: Number, default: null }
})

// Em pós-aprovação, apenas observação pode ser editada
const podeEditarCompleto = computed(() => {
  return props.podeEditar && !props.isPosAprovacao
})

// Produto em edição atual (para verificar status no modal)
const produtoEmEdicao = computed(() => {
  if (!editingProdutoId.value) return null
  return props.produtosPurchaseOrder.find(p => p.idPurchaseOrderProduto === editingProdutoId.value) || null
})

// Pode cancelar: produto Pendente + PO em pós-aprovação
const podeCancelarProduto = computed(() => {
  if (!props.isPosAprovacao || !produtoEmEdicao.value) return false
  return produtoEmEdicao.value.lstStatusProdutoPo === PO_PRODUTO_ST.Pendente
})

const emit = defineEmits([
  'alocar-produto',
  'remover-produto',
  'cancelar-produto',
  'show-notification'
])

// ============================================================
// ESTADO PARA MODO LIVRE (busca de produto do cadastro)
// ============================================================
const showBuscaProdutoModal = ref(false)
const produtoFornecedorModalRef = ref(null)

// ============================================================
// ESTADO DE EXIBIÇÃO DOS SALDOS (colapsável)
// ============================================================
const mostrarSaldos = ref(false)

// Guarda o ID da purchase order atual para detectar troca
const purchaseOrderAtualId = ref(null)

// Watcher dedicado para detectar troca de purchase order via prop idPurchaseOrder
watch(() => props.idPurchaseOrder, (novoId, idAnterior) => {
  if (idAnterior !== undefined && novoId !== idAnterior) {
    mostrarSaldos.value = false
    editingProdutoId.value = null
  }
  purchaseOrderAtualId.value = novoId
}, { immediate: true })

// Força o painel de saldos a fechar quando a PO não está mais em "Em Digitação"
watch(() => props.isPosAprovacao, (posAprovacao) => {
  if (posAprovacao) {
    mostrarSaldos.value = false
  }
}, { immediate: true })

// Abre o painel se a purchase order estiver vazia na primeira carga.
watch(() => props.produtosPurchaseOrder, (novaLista) => {
  if (novaLista.length === 0 && !mostrarSaldos.value && purchaseOrderAtualId.value !== null) {
    const jaInicializado = purchaseOrderAtualId.value !== null
    if (jaInicializado) {
      mostrarSaldos.value = true
    }
  }
}, { immediate: true })

// Quando o frame "Produtos da Purchase Order" é fechado, recolhe os saldos.
watch(() => props.accordionFechado, (fechado) => {
  if (fechado) {
    mostrarSaldos.value = false
  }
})

const toggleSaldos = () => {
  mostrarSaldos.value = !mostrarSaldos.value
}

const temSaldoPendente = computed(() => {
  return props.produtosComSaldo.some(p => (Number(p.qtdPendenteForn) || 0) > 0)
})

const isProdutoComSaldo = computed(() => {
  return fractionModalData.value.idContratoCompraProduto !== null
})

const temProdutosComSaldo = computed(() => {
  return props.produtosComSaldo.length > 0
})

const configBuscaProduto = computed(() => criarConfigBuscaProduto())

const produtosComSaldoOrdenados = computed(() => {
  return [...props.produtosComSaldo].sort((a, b) => {
    const saldoA = (Number(a.qtdPendenteForn) || 0) > 0 ? 0 : 1
    const saldoB = (Number(b.qtdPendenteForn) || 0) > 0 ? 0 : 1
    return saldoA - saldoB
  })
})

// ============================================================
// COMPUTED: Cálculos automáticos de preços
// ============================================================
const valorTotalOriginalCalc = computed(() => {
  return (fractionModalData.value.quantidadeForn * fractionModalData.value.valorUnitarioOriginal) || 0
})

const precoUnitarioNegociadoCalc = computed(() => {
  return Math.max(0, (fractionModalData.value.valorUnitarioOriginal - fractionModalData.value.valorUnitarioDesconto) || 0)
})

const valorTotalNegociadoCalc = computed(() => {
  return (fractionModalData.value.quantidadeForn * precoUnitarioNegociadoCalc.value) || 0
})

const valorTotalDescontoCalc = computed(() => {
  return (fractionModalData.value.quantidadeForn * fractionModalData.value.valorUnitarioDesconto) || 0
})

const mostrarMotivoDesconto = computed(() => {
  return fractionModalData.value.valorUnitarioDesconto > 0
})

// ============================================================
// DRAG & DROP
// ============================================================
const draggingOverId = ref(null)

const onDragStart = (event, item) => {
  if (!podeEditarCompleto.value) {
    event.preventDefault()
    return
  }
  const data = {
    idContratoCompraProduto: item.idContratoCompraProduto,
    idProduto: item.idProduto,
    nomeProduto: item.nomeProduto || '',
    sku: item.sku || '',
    lstUnidadeMedidaFornecedor: item.lstUnidadeMedidaFornecedor || '',
    lstUnidadeMedidaBasica: item.lstUnidadeMedidaBasica || '',
    fatorConversao: item.fatorConversao || 0,
    permiteConversaoVariavel: !!item.permiteConversaoVariavel,
    saldoDisponivelForn: Number(item.qtdPendenteForn) || 0,
    saldoDisponivelBase: Number(item.qtdPendenteBase) || 0
  }
  event.dataTransfer.setData('application/json', JSON.stringify(data))
  event.dataTransfer.effectAllowed = 'move'
}

const onDragEnter = () => {
  if (!podeEditarCompleto.value) return
  draggingOverId.value = '__purchaseOrder__'
}

const onDragOver = (event, id) => {
  if (!podeEditarCompleto.value) return
  event.preventDefault()
  draggingOverId.value = id
  event.dataTransfer.dropEffect = 'move'
}

const onDragLeave = (event) => {
  if (draggingOverId.value === '__purchaseOrder__') {
    const related = event.relatedTarget
    if (!related || !event.currentTarget.contains(related)) {
      draggingOverId.value = null
    }
  }
}

// ============================================================
// MODAL DE FRACIONAMENTO
// ============================================================
const showFractionModal = ref(false)
const editingProdutoId = ref(null)

const isEditing = computed(() => editingProdutoId.value !== null)

const fractionModalData = ref({
  idContratoCompraProduto: null,
  idProduto: null,
  nomeProduto: '',
  sku: '',
  lstUnidadeMedidaFornecedor: '',
  lstUnidadeMedidaBasica: '',
  fatorConversao: 0,
  permiteConversaoVariavel: false,
  saldoDisponivelForn: 0,
  saldoDisponivelBase: 0,
  quantidadeForn: 0,
  quantidadeBase: 0,
  valorUnitarioOriginal: 0,
  valorUnitarioDesconto: 0,
  lstMotivoDesconto: null,
  percentualTolerancia: null,
  observacao: ''
})

const onDrop = (event) => {
  draggingOverId.value = null
  if (!podeEditarCompleto.value) return

  const rawData = event.dataTransfer.getData('application/json')
  if (!rawData) return

  try {
    const data = JSON.parse(rawData)

    editingProdutoId.value = null

    fractionModalData.value = {
      idContratoCompraProduto: data.idContratoCompraProduto,
      idProduto: data.idProduto,
      nomeProduto: data.nomeProduto,
      sku: data.sku,
      lstUnidadeMedidaFornecedor: data.lstUnidadeMedidaFornecedor,
      lstUnidadeMedidaBasica: data.lstUnidadeMedidaBasica,
      fatorConversao: Number(data.fatorConversao) || 0,
      permiteConversaoVariavel: !!data.permiteConversaoVariavel,
      saldoDisponivelForn: Number(data.saldoDisponivelForn) || 0,
      saldoDisponivelBase: Number(data.saldoDisponivelBase) || 0,
      quantidadeForn: Number(data.saldoDisponivelForn) || 0,
      quantidadeBase: Number(data.saldoDisponivelBase) || 0,
      valorUnitarioOriginal: 0,
      valorUnitarioDesconto: 0,
      lstMotivoDesconto: null,
      percentualTolerancia: props.percentualToleranciaPadrao,
      observacao: ''
    }
    showFractionModal.value = true
  } catch (e) {
    console.error('Erro ao processar drop:', e)
  }
}

const { simular } = useConversaoQuantidade()

// Conversão centralizada (backend = fonte da verdade).
// Chamada no lost focus do campo de quantidade ou no botão "Calcular".
const calcularConversao = async (campo) => {
  const data = fractionModalData.value
  if (!data || !data.idProduto) return

  // Limita ao saldo disponível (quando o produto vem de saldo)
  if (isProdutoComSaldo.value) {
    const saldoForn = Number(data.saldoDisponivelForn) || 0
    const saldoBase = Number(data.saldoDisponivelBase) || 0
    if (Number(data.quantidadeForn) > saldoForn) data.quantidadeForn = saldoForn
    if (Number(data.quantidadeBase) > saldoBase) data.quantidadeBase = saldoBase
  }

  try {
    const resultado = await simular({
      idProduto: data.idProduto,
      idFornecedor: props.idParceiroNegocioFornecedor,
      quantidadeFornecedor: Number(data.quantidadeForn) || 0,
      quantidadeBase: Number(data.quantidadeBase) || 0,
      fatorConversao: Number(data.fatorConversao) || null,
      campoOrigem: campo === 'base' ? 'Base' : 'Fornecedor'
    })
    data.quantidadeForn = resultado.quantidadeFornecedor
    data.quantidadeBase = resultado.quantidadeBase
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao calcular a conversão.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// ============================================================
// CONVERSÃO FIXA AUTOMÁTICA (produto sem conversão variável)
// ============================================================
// Flag que evita loop entre os dois watches de conversão.
const isUpdatingFromConversion = ref(false)

// Watch de conversão automática: quantidadeForn → quantidadeBase
watch(() => fractionModalData.value.quantidadeForn, (novoValor) => {
  if (isUpdatingFromConversion.value) return
  const data = fractionModalData.value
  if (data.permiteConversaoVariavel) return
  const fator = Number(data.fatorConversao)
  if (fator > 0) {
    isUpdatingFromConversion.value = true
    data.quantidadeBase = parseFloat((Number(novoValor) * fator).toFixed(4))
    isUpdatingFromConversion.value = false
  }
})

// Watch de conversão automática: quantidadeBase → quantidadeForn
watch(() => fractionModalData.value.quantidadeBase, (novoValor) => {
  if (isUpdatingFromConversion.value) return
  const data = fractionModalData.value
  if (data.permiteConversaoVariavel) return
  const fator = Number(data.fatorConversao)
  if (fator > 0) {
    isUpdatingFromConversion.value = true
    data.quantidadeForn = parseFloat((Number(novoValor) / fator).toFixed(4))
    isUpdatingFromConversion.value = false
  }
})

// Limpa lstMotivoDesconto quando o desconto é zerado
watch(() => fractionModalData.value.valorUnitarioDesconto, (novoValor) => {
  if (!novoValor || novoValor <= 0) {
    fractionModalData.value.lstMotivoDesconto = null
  }
})

const ajustarQuantidade = (campo, delta) => {
  const data = fractionModalData.value
  const saldo = campo === 'quantidadeForn' ? data.saldoDisponivelForn : data.saldoDisponivelBase
  const atual = Number(data[campo]) || 0
  const novo = Math.max(0, Math.min(saldo, atual + delta))
  data[campo] = parseFloat(novo.toFixed(4))
}

const onEditarProduto = (prod) => {
  if (!props.podeEditar) return

  editingProdutoId.value = prod.idPurchaseOrderProduto

  const saldoOriginal = props.produtosComSaldo.find(sp => sp.idContratoCompraProduto === prod.idContratoCompraProduto)

  const saldoForn = Number(saldoOriginal?.qtdPendenteForn || 0)
  const saldoBase = Number(saldoOriginal?.qtdPendenteBase || 0)
  const qtdAlocadaForn = Number(prod.quantidadeUnFornecedorNegociada) || 0
  const qtdAlocadaBase = Number(prod.quantidadeUnBasicaNegociada) || 0

  const saldoDisponivelForn = parseFloat((saldoForn + qtdAlocadaForn).toFixed(4))
  const saldoDisponivelBase = parseFloat((saldoBase + qtdAlocadaBase).toFixed(4))

  fractionModalData.value = {
    idContratoCompraProduto: prod.idContratoCompraProduto,
    idProduto: prod.idProduto,
    nomeProduto: prod.nomeProduto || saldoOriginal?.nomeProduto || '',
    sku: prod.sku || saldoOriginal?.sku || '',
    lstUnidadeMedidaFornecedor: prod.lstUnidadeMedidaFornecedor || '',
    lstUnidadeMedidaBasica: prod.lstUnidadeMedidaBasica || '',
    fatorConversao: Number(prod.fatorConversao) || 0,
    permiteConversaoVariavel: !!prod.permiteConversaoVariavel,
    saldoDisponivelForn,
    saldoDisponivelBase,
    quantidadeForn: qtdAlocadaForn,
    quantidadeBase: qtdAlocadaBase,
    valorUnitarioOriginal: Number(prod.valorUnitarioOriginal) || 0,
    valorUnitarioDesconto: Number(prod.valorUnitarioDesconto) || 0,
    lstMotivoDesconto: prod.lstMotivoDesconto || null,
    lstPaisOrigem: prod.lstPaisOrigem || null,
    percentualTolerancia: prod.percentualTolerancia ?? props.percentualToleranciaPadrao,
    observacao: prod.observacao || ''
  }

  showFractionModal.value = true
}

const confirmarAlocacao = () => {
  const data = fractionModalData.value
  if (data.quantidadeForn <= 0 && data.quantidadeBase <= 0) {
    emit('show-notification', { message: 'Informe pelo menos uma quantidade maior que zero.', type: 'warning' })
    return
  }

  if (!data.lstPaisOrigem || String(data.lstPaisOrigem).trim() === '') {
    emit('show-notification', { message: 'O País de Origem é obrigatório.', type: 'warning' })
    return
  }

  const payload = {
    idContratoCompraProduto: data.idContratoCompraProduto || null,
    idProduto: data.idProduto,
    nomeProduto: data.nomeProduto,
    sku: data.sku,
    quantidadeUnFornecedorNegociada: data.quantidadeForn,
    quantidadeUnBasicaNegociada: data.quantidadeBase,
    fatorConversao: data.permiteConversaoVariavel ? 0 : data.fatorConversao,
    lstUnidadeMedidaFornecedor: data.lstUnidadeMedidaFornecedor,
    lstUnidadeMedidaBasica: data.lstUnidadeMedidaBasica,
    valorUnitarioOriginal: data.valorUnitarioOriginal,
    valorTotalOriginal: valorTotalOriginalCalc.value,
    valorUnitarioDesconto: data.valorUnitarioDesconto,
    valorTotalNegociado: valorTotalNegociadoCalc.value,
    valorTotalDesconto: valorTotalDescontoCalc.value,
    lstMotivoDesconto: data.lstMotivoDesconto,
    lstPaisOrigem: data.lstPaisOrigem || null,
    percentualTolerancia: data.percentualTolerancia,
    observacao: data.observacao,
    lstStatusProdutoPo: PO_PRODUTO_ST.EmDigitacao
  }

  if (isEditing.value) {
    payload.idPurchaseOrderProduto = editingProdutoId.value
  }

  emit('alocar-produto', payload)

  showFractionModal.value = false
  editingProdutoId.value = null
}

const fecharModal = () => {
  showFractionModal.value = false
  editingProdutoId.value = null
}

// ============================================================
// MODO LIVRE: Busca de produto do cadastro
// ============================================================
const navegarParaProdutoFornecedor = () => {
  useNavegacao().abrirRegra('PRODUTOS_FORNECEDOR', 'adicionar')
}

const handleProdutoFornecedorConfirm = (confirmed) => {
  if (confirmed) {
    navegarParaProdutoFornecedor()
  } else {
    emit('show-notification', {
      message: 'Não é possível inserir um produto em uma Purchase Order sem seu cadastro no Catálogo do Fornecedor (Produto no Fornecedor)',
      type: 'warning'
    })
  }
}

const handleProdutoCadastroSelecionado = async (produto) => {
  if (!produto) return
  showBuscaProdutoModal.value = false

  let lstUnidadeMedidaFornecedor = ''
  let lstUnidadeMedidaBasica = ''
  let fatorConversao = 0
  let permiteConversaoVariavel = produto.permiteConversaoVariavel || false

  try {
    const resProd = await api.get(`/Produtos/${produto.idProduto}`)
    const prodData = resProd.data
    permiteConversaoVariavel = prodData.permiteConversaoVariavel || false
    lstUnidadeMedidaBasica = prodData.lstUnidadeMedidaBase || ''
  } catch (e) {
    console.error('Erro ao buscar dados do produto:', e)
  }

  // Validação ProdutoFornecedor (só em modo livre)
  if (props.idParceiroNegocioFornecedor && props.modoAlocacao !== 'saldo') {
    try {
      const res = await api.get('/ProdutoFornecedores', {
        params: {
          idProduto: produto.idProduto,
          idFornecedor: props.idParceiroNegocioFornecedor,
          pageSize: 1000
        }
      })
      if (!res.data || res.data.length === 0) {
        emit('show-notification', {
          message: 'Produto sem cadastro no Catálogo do Fornecedor (Produto-Fornecedor).',
          type: 'warning'
        })
        nextTick(() => {
          if (produtoFornecedorModalRef.value) {
            produtoFornecedorModalRef.value.show()
          }
        })
        return
      }
      const link = res.data[0]
      lstUnidadeMedidaFornecedor = link.lstUnidadeFornecedor || ''
      fatorConversao = Number(link.fatorConversao) || 0
      lstUnidadeMedidaBasica = link.lstUnidadeMedidaBaseProduto || lstUnidadeMedidaBasica
    } catch (error) {
      console.error('Erro ao verificar vínculo ProdutoFornecedor:', error)
      emit('show-notification', {
        message: 'Erro ao verificar vínculo do produto com o fornecedor.',
        type: 'error'
      })
      return
    }
  }

  const saldoFallback = 100

  fractionModalData.value = {
    idContratoCompraProduto: null,
    idProduto: produto.idProduto,
    nomeProduto: produto.nome || produto.nomeProduto || '',
    sku: produto.sku || '',
    lstUnidadeMedidaFornecedor,
    lstUnidadeMedidaBasica,
    fatorConversao,
    permiteConversaoVariavel,
    saldoDisponivelForn: saldoFallback,
    saldoDisponivelBase: saldoFallback,
    quantidadeForn: 0,
    quantidadeBase: 0,
    valorUnitarioOriginal: 0,
    valorUnitarioDesconto: 0,
    lstMotivoDesconto: null,
    lstPaisOrigem: null,
    percentualTolerancia: props.percentualToleranciaPadrao,
    observacao: ''
  }

  showFractionModal.value = true
}

// ============================================================
// HELPERS
// ============================================================
const getTextoUnidade = (id) => {
  const lista = props.listas.LstUnidadeMedidaFornecedor || props.listas.LstUnidadeMedidaBase || props.listas.LstUnidadeMedidaBasica || []
  const un = lista.find(u => u.idListaClienteItem === id)
  return un ? un.textoLista : (id || '-')
}

const getTextoMoeda = () => {
  if (!props.listas.LstMoeda) return props.lstMoeda || 'MOEDA'
  if (!props.lstMoeda) return 'MOEDA'
  const moeda = props.listas.LstMoeda.find(m => m.idListaClienteItem === props.lstMoeda)
  return moeda ? moeda.textoLista : props.lstMoeda
}

const formatarQtd = (valor) => {
  if (valor === null || valor === undefined || valor === '') return '0'
  const num = Number(valor)
  if (isNaN(num)) return '0'
  return num.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4
  })
}

const formatarDecimal = (valor) => {
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor || 0)
}

const confirmarExclusao = (prod) => {
  emit('remover-produto', { idPurchaseOrderProduto: prod.idPurchaseOrderProduto })
}
</script>

<template>
  <div class="purchase-order-smart-mode">
    <!-- ============================================================ -->
    <!-- PAINEL DE SALDOS (colapsável, à esquerda) -->
    <!-- ============================================================ -->
    <div v-if="modoAlocacao === 'saldo'" class="saldos-panel" :class="{ 'saldos-visible': mostrarSaldos }">
      <div class="saldos-panel-inner">
        <div class="saldos-header">
          <div class="saldos-header-top">
            <h5 class="mb-0 d-flex align-items-center gap-2">
              <i class="bi bi-box-seam"></i>
              Saldos do Contrato
              <span v-if="produtosComSaldo.length > 0" class="badge bg-info bg-opacity-10 text-info-emphasis border border-info-subtle ms-1">
                {{ produtosComSaldo.length }}
              </span>
            </h5>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary border-0 p-1"
              title="Fechar painel de saldos"
              @click="toggleSaldos"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          <small class="text-muted">Arraste cards para alocar na purchase order</small>
        </div>
        <div class="saldos-body">
          <template v-if="produtosComSaldo.length === 0">
            <div class="empty-state">
              <i class="bi bi-inbox fs-1 d-block mb-2 text-muted"></i>
              <p class="mb-0">Nenhum saldo pendente no contrato.</p>
            </div>
          </template>
          <template v-else>
            <div
              v-for="item in produtosComSaldoOrdenados"
              :key="item.idContratoCompraProduto"
              draggable="true"
              @dragstart="onDragStart($event, item)"
              class="draggable-card card-saldo-produto"
              :class="{ 'card-saldo-esgotado': (Number(item.qtdPendenteForn) || 0) <= 0 }"
            >
              <div class="card-title">{{ item.nomeProduto || 'Produto #' + item.idProduto }}</div>
              <div class="card-sku" v-if="item.sku">
                <code>{{ item.sku }}</code>
              </div>
              <div class="card-saldo">
                <div class="saldo-row">
                  <span class="saldo-label">Saldo (Forn):</span>
                  <span class="saldo-value">{{ formatarQtd(item.qtdPendenteForn) }} {{ getTextoUnidade(item.lstUnidadeMedidaFornecedor) }}</span>
                </div>
                <div class="saldo-row">
                  <span class="saldo-label">Saldo (Base):</span>
                  <span class="saldo-value">{{ formatarQtd(item.qtdPendenteBase) }} {{ getTextoUnidade(item.lstUnidadeMedidaBasica) }}</span>
                </div>
              </div>
              <!-- O contrato não armazena preço — o preço é definido na Purchase Order -->
              <div v-if="item.permiteConversaoVariavel" class="card-flag-conversao">
                <i class="bi bi-arrow-left-right me-1"></i> Conversão Variável
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- COLUNA PRINCIPAL: PRODUTOS ALOCADOS NA PURCHASE ORDER -->
    <!-- ============================================================ -->
    <div class="po-main" :class="{ 'po-main-expanded': !mostrarSaldos }">
      <!-- Toolbar com botões de ação -->
      <div class="po-main-toolbar">
        <h5 class="mb-0 d-flex align-items-center gap-2">
          <i class="bi bi-list-check"></i>
          Produtos Alocados na Purchase Order
          <span v-if="produtosPurchaseOrder.length > 0" class="badge bg-info bg-opacity-10 text-info-emphasis border border-info-subtle ms-1">
            {{ produtosPurchaseOrder.length }} {{ produtosPurchaseOrder.length === 1 ? 'produto' : 'produtos' }}
          </span>
        </h5>
        <div class="d-flex gap-2 align-items-center">
          <!-- Botão p/ abrir painel de saldos (modo saldo) -->
          <button
            v-if="modoAlocacao === 'saldo' && temSaldoPendente && podeEditarCompleto"
            type="button"
            class="btn btn-sm btn-info text-white fw-bold"
            @click="toggleSaldos"
            :title="mostrarSaldos ? 'Fechar saldos' : 'Adicionar Produtos à Purchase Order'"
          >
            <i class="bi" :class="mostrarSaldos ? 'bi-x-lg' : 'bi-plus-lg'"></i>
            {{ mostrarSaldos ? 'Fechar Saldos' : 'Adicionar Produtos à PO' }}
          </button>
          <!-- Botão p/ buscar produto do cadastro (modo livre) -->
          <button
            v-if="modoAlocacao !== 'saldo' && !temProdutosComSaldo && podeEditarCompleto"
            type="button"
            class="btn btn-sm btn-info text-white fw-bold"
            @click="showBuscaProdutoModal = true"
            title="Adicionar Produto do Cadastro"
          >
            <i class="bi bi-search me-1"></i> Adicionar Produto do Cadastro
          </button>
        </div>
      </div>

      <!-- ModalDeBusca para selecionar produto do cadastro (modo livre) -->
      <ModalDeBusca
        v-if="showBuscaProdutoModal && modoAlocacao !== 'saldo' && !temProdutosComSaldo"
        :configuracao="configBuscaProduto"
        tamanho="lg"
        @item-selecionado="handleProdutoCadastroSelecionado"
        @fechar="showBuscaProdutoModal = false"
      />

      <div
        class="po-main-body"
        :class="{
          'dropzone-ativa': mostrarSaldos,
          'drag-over': draggingOverId === '__purchaseOrder__'
        }"
        :style="mostrarSaldos ? { minHeight: '200px' } : {}"
        @dragenter="mostrarSaldos && onDragEnter($event)"
        @dragover.prevent="mostrarSaldos && onDragOver($event, '__purchaseOrder__')"
        @dragleave="mostrarSaldos && onDragLeave($event)"
        @drop.prevent="mostrarSaldos && onDrop($event)"
      >

        <template v-if="produtosPurchaseOrder.length === 0">
          <!-- Modo saldo com painel aberto -->
          <div v-if="mostrarSaldos && modoAlocacao === 'saldo'" class="empty-state">
            <i class="bi bi-download fs-1 d-block mb-2 text-muted"></i>
            <p class="mb-0">Arraste produtos do painel ao lado</p>
          </div>
          <!-- Modo livre com painel aberto -->
          <div v-else-if="mostrarSaldos && modoAlocacao !== 'saldo'" class="empty-state">
            <i class="bi bi-plus-circle fs-1 d-block mb-2 text-muted"></i>
            <p class="mb-0">Adicione produtos manualmente</p>
            <button v-if="podeEditarCompleto" type="button"
              class="btn btn-sm btn-info text-white fw-bold mt-3"
              @click="showBuscaProdutoModal = true">
              <i class="bi bi-search me-1"></i> Adicionar Produto do Cadastro
            </button>
          </div>
          <!-- Painel recolhido -->
          <div v-else class="empty-state">
            <i class="bi bi-inbox fs-1 d-block mb-2 text-muted"></i>
            <p class="mb-0">Nenhum produto alocado nesta purchase order.</p>
            <!-- Botão modo saldo -->
            <button v-if="modoAlocacao === 'saldo' && temSaldoPendente && podeEditarCompleto"
              type="button" class="btn btn-sm btn-info text-white fw-bold mt-3"
              @click="toggleSaldos">
              <i class="bi bi-plus-lg me-1"></i> Adicionar Produtos à PO
            </button>
            <!-- Botão modo livre -->
            <button v-if="modoAlocacao !== 'saldo' && !temProdutosComSaldo && podeEditarCompleto"
              type="button" class="btn btn-sm btn-info text-white fw-bold mt-3"
              @click="showBuscaProdutoModal = true">
              <i class="bi bi-search me-1"></i> Adicionar Produto do Cadastro
            </button>
          </div>
        </template>

        <template v-if="produtosPurchaseOrder.length > 0">
          <div class="po-produtos-grid">
            <div
              v-for="prod in produtosPurchaseOrder"
              :key="prod.idPurchaseOrderProduto"
              class="po-produto-card"
            >
              <div class="po-produto-header">
                <div class="po-produto-info">
                  <div class="po-produto-nome">{{ prod.nomeProduto || 'Produto #' + prod.idProduto }}</div>
                  <div class="po-produto-sku" v-if="prod.sku">
                    <code>{{ prod.sku }}</code>
                  </div>
                </div>
                <div class="po-produto-actions">
                  <button
                    v-if="podeEditar"
                    type="button"
                    class="btn btn-sm btn-outline-primary border-0"
                    title="Editar produto na purchase order"
                    @click="onEditarProduto(prod)"
                  >
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button
                    v-if="podeEditarCompleto"
                    type="button"
                    class="btn btn-sm btn-outline-danger border-0"
                    title="Remover produto da purchase order"
                    @click="confirmarExclusao(prod)"
                  >
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
              <div class="po-produto-detalhes">
                <div class="detalhe-item">
                  <span class="detalhe-label">Qtd (Forn):</span>
                  <span class="detalhe-value">{{ formatarQtd(prod.quantidadeUnFornecedorNegociada) }} {{ getTextoUnidade(prod.lstUnidadeMedidaFornecedor) }}</span>
                </div>
                <div class="detalhe-item">
                  <span class="detalhe-label">Qtd (Base):</span>
                  <span class="detalhe-value">{{ formatarQtd(prod.quantidadeUnBasicaNegociada) }} {{ getTextoUnidade(prod.lstUnidadeMedidaBasica) }}</span>
                </div>
                <div class="detalhe-item" v-if="Number(prod.valorUnitarioOriginal) > 0">
                  <span class="detalhe-label">Preço Unit.:</span>
                  <span class="detalhe-value">{{ formatarDecimal(prod.valorUnitarioOriginal) }}</span>
                </div>
                <div class="detalhe-item detalhe-total">
                  <span class="detalhe-label">Total ({{ getTextoMoeda() }}):</span>
                  <span class="detalhe-value fw-bold">{{ formatarDecimal(prod.valorTotalNegociado ?? ((Number(prod.quantidadeUnFornecedorNegociada) || 0) * (Number(prod.valorUnitarioOriginal) || 0))) }}</span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- MODAL DE FRACIONAMENTO (ModalFormularioV2) -->
    <!-- ============================================================ -->
    <ModalFormularioV2
      v-model="showFractionModal"
      :titulo="(isEditing ? 'Editar' : 'Alocar') + ' Produto na Purchase Order'"
      tamanho="lg"
      :loading="false"
      @confirmar="confirmarAlocacao"
    >
      <template #header>
        <h5 class="modal-title-v2 mb-0">
          <i class="bi me-2" :class="isEditing ? 'bi-pencil' : 'bi-arrows-split'"></i>
          {{ isEditing ? 'Editar Produto na Purchase Order' : 'Alocar Produto na Purchase Order' }}
        </h5>
      </template>

      <!-- Info de Origem (2 linhas: SKU+Descrição à esquerda, Saldos à direita) -->
      <div class="modal-info-box mb-3">
        <div class="info-left">
          <div class="info-sku" v-if="fractionModalData.sku">
            <code class="fw-bold">{{ fractionModalData.sku }}</code>
          </div>
          <div class="info-produto-nome">{{ fractionModalData.nomeProduto }}</div>
        </div>
        <div v-if="isProdutoComSaldo" class="info-right">
          <div class="info-item">
            <span class="info-label">Saldo (Forn):</span>
            <span class="info-valor">{{ formatarQtd(fractionModalData.saldoDisponivelForn) }} {{ getTextoUnidade(fractionModalData.lstUnidadeMedidaFornecedor) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Saldo (Base):</span>
            <span class="info-valor">{{ formatarQtd(fractionModalData.saldoDisponivelBase) }} {{ getTextoUnidade(fractionModalData.lstUnidadeMedidaBasica) }}</span>
          </div>
        </div>
      </div>

      <!-- Aviso de conversão -->
      <div v-if="!fractionModalData.permiteConversaoVariavel && Number(fractionModalData.fatorConversao) > 0"
        class="alert alert-info py-2 mb-3"
      >
        <i class="bi bi-info-circle me-2"></i>
        Fator de conversão: {{ fractionModalData.fatorConversao }}. As quantidades são sincronizadas automaticamente.
      </div>
      <div
        v-else-if="fractionModalData.permiteConversaoVariavel || Number(fractionModalData.fatorConversao) === 0"
        class="alert alert-warning py-2 mb-3"
      >
        <i class="bi bi-exclamation-triangle me-2"></i>
        Produto com conversão variável. Informe as quantidades independentemente.
      </div>

      <!-- Sliders de Quantidade -->
      <div class="sliders-stacked">
        <!-- Qtd Fornecedor -->
        <div class="slider-section">
          <div class="slider-section-header">
            <i class="bi bi-box-seam me-1"></i>
            Qtd. Fornecedor
            <small class="text-muted">({{ getTextoUnidade(fractionModalData.lstUnidadeMedidaFornecedor) }})</small>
          </div>
          <div class="slider-row">
            <input type="range" class="form-range slider-row-main"
                   :min="0" :max="fractionModalData.saldoDisponivelForn" step="0.1"
                   v-model.number="fractionModalData.quantidadeForn"
                   :disabled="!podeEditarCompleto" />
            <div class="slider-row-controls">
              <div class="slider-row-buttons">
                <button type="button" class="btn btn-sm btn-outline-secondary slider-btn"
                        title="Diminuir 1 unidade"
                        @click="ajustarQuantidade('quantidadeForn', -1)"
                        :disabled="!podeEditarCompleto || fractionModalData.quantidadeForn < 1">
                  <i class="bi bi-dash-lg"></i>
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary slider-btn slider-btn-decimal"
                        title="Diminuir 0.1"
                        @click="ajustarQuantidade('quantidadeForn', -0.1)"
                        :disabled="!podeEditarCompleto || fractionModalData.quantidadeForn < 0.1">
                  <i class="bi bi-dash-lg"></i> 0,1
                </button>
              </div>
              <div class="slider-row-value">
                <input type="number" class="form-control form-control-sm"
                       :min="0"
                       :max="isProdutoComSaldo ? fractionModalData.saldoDisponivelForn : undefined"
                       step="0.01"
                       v-model.number="fractionModalData.quantidadeForn"
                       :disabled="!podeEditarCompleto"
                       @blur="calcularConversao('fornecedor')" />
                <span v-if="isProdutoComSaldo" class="slider-max">/ {{ formatarQtd(fractionModalData.saldoDisponivelForn) }}</span>
              </div>
              <div class="slider-row-buttons">
                <button type="button" class="btn btn-sm btn-outline-secondary slider-btn"
                        title="Aumentar 1 unidade"
                        @click="ajustarQuantidade('quantidadeForn', 1)"
                        :disabled="!podeEditarCompleto || (isProdutoComSaldo && fractionModalData.quantidadeForn >= fractionModalData.saldoDisponivelForn)">
                  <i class="bi bi-plus-lg"></i>
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary slider-btn slider-btn-decimal"
                        title="Aumentar 0.1"
                        @click="ajustarQuantidade('quantidadeForn', 0.1)"
                        :disabled="!podeEditarCompleto || (isProdutoComSaldo && fractionModalData.quantidadeForn >= fractionModalData.saldoDisponivelForn)">
                  <i class="bi bi-plus-lg"></i> 0,1
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Qtd Básica -->
        <div class="slider-section">
          <div class="slider-section-header">
            <i class="bi bi-box me-1"></i>
            Qtd. Básica
            <small class="text-muted">({{ getTextoUnidade(fractionModalData.lstUnidadeMedidaBasica) }})</small>
            <button
              type="button"
              class="btn btn-sm calcular-btn"
              title="Calcular conversão de unidades"
              :disabled="!podeEditarCompleto"
              @click="calcularConversao('base')"
            >
              <i class="bi bi-calculator me-1"></i> Calcular
            </button>
          </div>
          <div class="slider-row">
            <input type="range" class="form-range slider-row-main"
                   :min="0" :max="fractionModalData.saldoDisponivelBase" step="0.1"
                   v-model.number="fractionModalData.quantidadeBase"
                   :disabled="!podeEditarCompleto" />
            <div class="slider-row-controls">
              <div class="slider-row-buttons">
                <button type="button" class="btn btn-sm btn-outline-secondary slider-btn"
                        title="Diminuir 1 unidade"
                        @click="ajustarQuantidade('quantidadeBase', -1)"
                        :disabled="!podeEditarCompleto || fractionModalData.quantidadeBase < 1">
                  <i class="bi bi-dash-lg"></i>
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary slider-btn slider-btn-decimal"
                        title="Diminuir 0.1"
                        @click="ajustarQuantidade('quantidadeBase', -0.1)"
                        :disabled="!podeEditarCompleto || fractionModalData.quantidadeBase < 0.1">
                  <i class="bi bi-dash-lg"></i> 0,1
                </button>
              </div>
              <div class="slider-row-value">
                <input type="number" class="form-control form-control-sm"
                       :min="0"
                       :max="isProdutoComSaldo ? fractionModalData.saldoDisponivelBase : undefined"
                       step="0.01"
                       v-model.number="fractionModalData.quantidadeBase"
                       :disabled="!podeEditarCompleto"
                       @blur="calcularConversao('base')" />
                <span v-if="isProdutoComSaldo" class="slider-max">/ {{ formatarQtd(fractionModalData.saldoDisponivelBase) }}</span>
              </div>
              <div class="slider-row-buttons">
                <button type="button" class="btn btn-sm btn-outline-secondary slider-btn"
                        title="Aumentar 1 unidade"
                        @click="ajustarQuantidade('quantidadeBase', 1)"
                        :disabled="!podeEditarCompleto || (isProdutoComSaldo && fractionModalData.quantidadeBase >= fractionModalData.saldoDisponivelBase)">
                  <i class="bi bi-plus-lg"></i>
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary slider-btn slider-btn-decimal"
                        title="Aumentar 0.1"
                        @click="ajustarQuantidade('quantidadeBase', 0.1)"
                        :disabled="!podeEditarCompleto || (isProdutoComSaldo && fractionModalData.quantidadeBase >= fractionModalData.saldoDisponivelBase)">
                  <i class="bi bi-plus-lg"></i> 0,1
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ============================================================ -->
      <!-- PREÇOS: 3 linhas (Unitários | Totais | Tolerância/Observação) -->
      <!-- ============================================================ -->
      <div class="precos-grid mt-3">
        <!-- Linha 1: Valores Unitários -->
        <div class="row g-2">
          <div class="col-md-4">
            <CampoInput
              id="modal-valorUnitarioOriginal"
              :label="'Valor Unit. Original (' + getTextoMoeda() + ')'"
              v-model="fractionModalData.valorUnitarioOriginal"
              type="moeda"
              :disabled="!podeEditarCompleto"
            />
          </div>
          <div class="col-md-4">
            <CampoInput
              id="modal-valorUnitarioDesconto"
              :label="'Desconto Unitário (' + getTextoMoeda() + ')'"
              v-model="fractionModalData.valorUnitarioDesconto"
              type="moeda"
              :disabled="!podeEditarCompleto"
            />
          </div>
          <div class="col-md-4">
            <CampoInput
              id="modal-precoUnitarioNegociado"
              :label="'Preço Unit. Negociado (' + getTextoMoeda() + ')'"
              :model-value="precoUnitarioNegociadoCalc"
              type="moeda"
              disabled
            />
          </div>
        </div>

        <!-- Linha 2: Valores Totais -->
        <div class="row g-2 mt-2">
          <div class="col-md-4">
            <CampoInput
              id="modal-valorTotalOriginal"
              :label="'Valor Total Original (' + getTextoMoeda() + ')'"
              :model-value="valorTotalOriginalCalc"
              type="moeda"
              disabled
            />
          </div>
          <div class="col-md-4">
            <CampoInput
              id="modal-valorTotalDesconto"
              :label="'Total Desconto (' + getTextoMoeda() + ')'"
              :model-value="valorTotalDescontoCalc"
              type="moeda"
              disabled
            />
          </div>
          <div class="col-md-4">
            <CampoInput
              id="modal-valorTotalNegociado"
              :label="'Total Negociado (' + getTextoMoeda() + ')'"
              :model-value="valorTotalNegociadoCalc"
              type="moeda"
              disabled
            />
          </div>
        </div>

        <!-- Linha 3: Tolerância, Motivo Desconto, Observação -->
        <div class="row g-2 mt-2">
          <div class="col-md-3">
            <CampoInput
              id="modal-percentualTolerancia"
              label="% Tolerância"
              v-model="fractionModalData.percentualTolerancia"
              type="percentual"
              :disabled="!podeEditarCompleto"
            />
          </div>
          <div class="col-md-5">
            <CampoBuscaLista
              id="LstMotivoDesconto"
              label="Motivo do Desconto"
              v-model="fractionModalData.lstMotivoDesconto"
              tabela="PurchaseOrderProduto"
              :disabled="!podeEditarCompleto || !mostrarMotivoDesconto"
            />
          </div>
          <div class="col-md-4">
            <CampoBuscaLista
              id="LstPaisOrigem"
              label="País de Origem"
              v-model="fractionModalData.lstPaisOrigem"
              tabela="PurchaseOrderProduto"
              required
              :disabled="!podeEditarCompleto"
            />
          </div>
          <div class="col-md-12">
            <CampoInput
              id="modal-observacao"
              label="Observação"
              v-model="fractionModalData.observacao"
              type="textarea"
              :disabled="!podeEditarCompleto"
            />
          </div>
        </div>
      </div>

      <template #footer>
        <div class="d-flex gap-2 w-100 justify-content-between">
          <div>
            <button
              v-if="podeCancelarProduto"
              type="button"
              class="btn btn-outline-danger fw-bold"
              @click="emit('cancelar-produto', { idPurchaseOrderProduto: editingProdutoId })"
            >
              <i class="bi bi-x-circle me-1"></i>
              Cancelar Produto
            </button>
          </div>
          <div class="d-flex gap-2">
            <button type="button" class="btn btn-outline-secondary" @click="fecharModal">
              Cancelar
            </button>
            <button type="button" class="btn btn-info text-white fw-bold" @click="confirmarAlocacao" autofocus>
              <i class="bi me-1" :class="isEditing ? 'bi-check-lg' : 'bi-check-circle'"></i>
              {{ isEditing ? 'Salvar Alterações' : 'Confirmar Alocação' }}
            </button>
          </div>
        </div>
      </template>
    </ModalFormularioV2>

    <!-- ConfirmationModal para Produto sem cadastro no Fornecedor -->
    <Teleport to="body">
      <ConfirmationModal
        ref="produtoFornecedorModalRef"
        title="Produto sem Cadastro no Fornecedor"
        message="Este produto não possui cadastro no Catálogo do Fornecedor (Produto-Fornecedor). Deseja ir para o cadastro?"
        confirm-button-text="Sim, ir para cadastro"
        cancel-button-text="Não, cancelar"
        @confirmed="handleProdutoFornecedorConfirm"
      />
    </Teleport>
  </div>
</template>

<style scoped>
/* ============================================================ */
/* LAYOUT PRINCIPAL - PAINEL COLABSÁVEL + CONTEÚDO PRINCIPAL */
/* ============================================================ */
.purchase-order-smart-mode {
  position: relative;
  display: flex;
  gap: 0;
  min-height: 300px;
  align-items: flex-start;
}

/* ============================================================ */
/* PAINEL DE SALDOS (colapsável à esquerda) */
/* ============================================================ */
.saldos-panel {
  width: 0;
  overflow: hidden;
  flex-shrink: 0;
  transition: width 0.3s ease;
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  background: var(--bs-body-bg);
  height: 100%;
  min-height: 300px;
}

.saldos-panel.saldos-visible {
  width: 380px;
  margin-right: 16px;
}

.saldos-panel-inner {
  width: 380px;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 300px;
}

.saldos-header {
  padding: 12px 16px;
  background: var(--bs-tertiary-bg);
  border-bottom: 1px solid var(--bs-border-color);
  flex-shrink: 0;
}

.saldos-header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.saldos-header h5 {
  font-size: 0.9rem;
  font-weight: 700;
}

.saldos-header small {
  font-size: 0.7rem;
}

.saldos-body {
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  max-height: 500px;
}

/* ============================================================ */
/* COLUNA PRINCIPAL */
/* ============================================================ */
.po-main {
  flex: 1;
  min-width: 0;
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  background: var(--bs-body-bg);
  overflow: hidden;
}

.po-main-expanded {
  width: 100%;
}

.po-main-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: var(--bs-tertiary-bg);
  border-bottom: 1px solid var(--bs-border-color);
}

.po-main-toolbar h5 {
  font-size: 0.9rem;
  font-weight: 700;
}

.po-main-body {
  padding: 12px;
  overflow-y: auto;
  max-height: 500px;
  position: relative;
}

/* Dropzone quando saldos estão visíveis */
.po-main-body.dropzone-ativa {
  border: 2px dashed var(--bs-border-color);
  border-radius: 8px;
  margin: 8px;
  transition: all 0.3s ease;
  min-height: 200px;
}

.po-main-body.dropzone-ativa.drag-over {
  border-color: var(--bs-info);
  background-color: var(--bs-info-bg-subtle);
  transform: scale(1.01);
}

/* ============================================================ */
/* EMPTY STATE */
/* ============================================================ */
.empty-state {
  text-align: center;
  padding: 40px 16px;
  color: var(--bs-secondary-color);
}

.empty-state p {
  font-size: 0.85rem;
}

/* ============================================================ */
/* CARDS DRAGGABLE (Painel de Saldos) */
/* ============================================================ */
.draggable-card {
  cursor: grab;
  padding: 10px 12px;
  margin-bottom: 8px;
  border: 1px solid var(--bs-border-color);
  border-left: 4px solid var(--bs-info);
  border-radius: 6px;
  background: var(--bs-body-bg);
  transition: transform 0.2s, box-shadow 0.2s;
}

.draggable-card:active {
  cursor: grabbing;
  transform: scale(0.98);
}

.draggable-card:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.card-saldo-esgotado {
  opacity: 0.5;
  border-left-color: var(--bs-danger);
  cursor: not-allowed;
}

.card-title {
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--bs-body-color);
  margin-bottom: 2px;
}

.card-sku {
  font-size: 0.75rem;
  margin-bottom: 6px;
}

.card-saldo {
  font-size: 0.75rem;
}

.saldo-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1px 0;
}

.saldo-label {
  color: var(--bs-secondary-color);
  font-size: 0.72rem;
}

.saldo-value {
  font-weight: 600;
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 0.75rem;
}

.card-preco {
  font-size: 0.72rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 0;
  border-top: 1px solid var(--bs-border-color-translucent);
  margin-top: 4px;
  padding-top: 4px;
}

.card-flag-conversao {
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--bs-warning-text-emphasis);
  background: var(--bs-warning-bg-subtle);
  padding: 2px 6px;
  border-radius: 4px;
  margin-top: 4px;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

/* ============================================================ */
/* CARDS DE PRODUTOS ALOCADOS (Grid de 4) */
/* ============================================================ */
.po-produtos-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.po-produto-card {
  padding: 10px 12px;
  border: 1px solid var(--bs-border-color);
  border-left: 4px solid var(--bs-success);
  border-radius: 6px;
  background: var(--bs-body-bg);
}

.po-produto-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 6px;
  gap: 4px;
}

.po-produto-info {
  flex: 1;
  min-width: 0;
}

.po-produto-actions {
  display: flex;
  gap: 2px;
  flex-shrink: 0;
}

.po-produto-actions .btn {
  font-size: 0.7rem;
  padding: 2px 6px;
  line-height: 1;
}

.po-produto-nome {
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--bs-body-color);
}

.po-produto-sku {
  font-size: 0.72rem;
}

.po-produto-descricao {
  font-size: 0.72rem;
  color: var(--bs-secondary-color);
  display: block;
  margin-top: 1px;
}

.po-produto-detalhes {
  font-size: 0.75rem;
}

.detalhe-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1px 0;
}

.detalhe-label {
  color: var(--bs-secondary-color);
  font-size: 0.72rem;
}

.detalhe-value {
  font-weight: 600;
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 0.75rem;
}

.detalhe-total {
  border-top: 1px solid var(--bs-border-color-translucent);
  margin-top: 4px;
  padding-top: 4px;
}

.modal-info-box {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4px 24px;
  background: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
}

.info-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.info-right {
  display: flex;
  flex-direction: column;
  gap: 2px;
  justify-content: center;
  text-align: right;
}

.info-right .info-item {
  justify-content: flex-end;
}

.info-right .info-label {
  min-width: auto;
}

.info-sku code {
  font-size: 0.95rem;
  color: var(--bs-danger);
  font-weight: 700;
}

.info-produto-nome {
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--bs-body-color);
  word-break: break-word;
}

.info-item {
  display: flex;
  gap: 8px;
  font-size: 0.85rem;
  padding: 2px 0;
}

.info-item .info-label {
  font-weight: 600;
  min-width: 90px;
  color: var(--bs-secondary-color);
}

.info-item .info-valor {
  color: var(--bs-body-color);
}

.preco-total-info {
  background: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  padding: 10px 12px;
}

/* ============================================================ */
/* SLIDERS */
/* ============================================================ */
.sliders-stacked {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.slider-section {
  background: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  padding: 12px;
}

.slider-section-header {
  font-weight: 700;
  font-size: 0.8rem;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.slider-section-header small {
  font-weight: 400;
  font-size: 0.7rem;
}

.slider-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.slider-row-main {
  flex: 1;
  min-width: 0;
  margin: 0;
}

.slider-row-controls {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  flex-shrink: 0;
}

.slider-row-buttons {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.slider-row-value {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  min-width: 80px;
}

.slider-row-value .form-control-sm {
  width: 100%;
  font-family: 'Consolas', 'Courier New', monospace;
  font-weight: 600;
  text-align: center;
}

.slider-row-value .slider-max {
  font-size: 0.6rem;
  color: var(--bs-secondary-color);
  font-family: 'Consolas', 'Courier New', monospace;
  white-space: nowrap;
}

.slider-btn {
  font-size: 0.6rem;
  padding: 2px 8px;
  line-height: 1.3;
  border-width: 1px;
  white-space: nowrap;
  min-width: 36px;
}

.slider-btn-decimal {
  font-size: 0.55rem;
  opacity: 0.8;
  min-width: 32px;
}

/* Botão Calcular — compacto, sem borda e com fundo de alta transparência */
.calcular-btn {
  margin-left: auto;
  padding: 0 8px;
  font-size: 0.68rem;
  line-height: 1.6;
  border: 0;
  border-radius: 4px;
  color: var(--bs-primary);
  background-color: rgba(var(--bs-primary-rgb), 0.12);
}

.calcular-btn:hover:not(:disabled) {
  background-color: rgba(var(--bs-primary-rgb), 0.22);
}

.calcular-btn:disabled {
  opacity: 0.5;
}

/* ============================================================ */
/* RESPONSIVIDADE */
/* ============================================================ */
@media (max-width: 1400px) {
  .po-produtos-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 1200px) {
  .po-produtos-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .saldos-panel.saldos-visible {
    width: 320px;
  }
}

@media (max-width: 992px) {
  .purchase-order-smart-mode {
    flex-direction: column;
  }

  .saldos-panel.saldos-visible {
    width: 100%;
    margin-right: 0;
    margin-bottom: 16px;
  }

  .po-produtos-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .po-produtos-grid {
    grid-template-columns: 1fr;
  }
}
</style>
